
import React, { useCallback, useState, useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFormSyncToHash } from '@/hooks/useFormSyncToHash';
import { supabase } from '@/integrations/supabase/client';
import { useCommunity } from '@/contexts/CommunityContext';
import { Loader2 } from 'lucide-react';
import { useBusiness } from '@/contexts/BusinessContext';

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Business name must be at least 2 characters.",
  }),
  slug: z.string().min(3, {
    message: "Slug must be at least 3 characters.",
  }),
  category_id: z.string().min(1, {
    message: "Please select a category."
  }),
  description: z.string().min(10, {
    message: "Description must be at least 10 characters.",
  }).max(500, {
    message: "Description cannot exceed 500 characters."
  }),
});

type FormValues = z.infer<typeof formSchema>;

interface BusinessBasicInfoFormProps {
  formData: any;
  updateFormData: (data: Partial<any>) => void;
  categories: any[];
  onNext: () => void;
}

const BusinessBasicInfoForm = ({
  formData,
  updateFormData,
  categories,
  onNext
}: BusinessBasicInfoFormProps) => {
  const { currentCommunity } = useCommunity();
  const { business } = useBusiness();
  const [checking, setChecking] = useState(false);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: formData.name || '',
      slug: formData.slug || '',
      category_id: formData.category_id || '',
      description: formData.description || '',
    },
  });
  
  // Use the custom hook to sync form data to URL hash
  useFormSyncToHash(form.watch, formData, updateFormData);


  // Function to check base slug uniqueness
  const checkSlugUnique = useCallback(
    async (baseSlug: string) => {
      if (!baseSlug || !currentCommunity?.id) return true;
      
      const { data, error } = await supabase.rpc(
        "check_base_slug_unique_in_community", 
        { 
          _community_id: currentCommunity.id, 
          _base_slug: baseSlug,
          _business_id: business?.userBusinessId || null
        }
      );
      
      if (error) {
        console.error("Error checking base slug uniqueness:", error);
        return false;
      }
      
      // Handle JSON response format {unique: boolean}
      return data?.unique === true;
    },
    [currentCommunity, business]
  );

  // Generate slug when business name changes using RPC function with debouncing
  useEffect(() => {
    const businessName = form.watch("name");
    // Only consider first 50 characters since RPC function truncates at 50
    const relevantBusinessName = businessName?.substring(0, 50) || '';
    let timer: NodeJS.Timeout | null = null;
    
    if (relevantBusinessName && relevantBusinessName.length >= 2) {
      timer = setTimeout(async () => {
        try {
          const { data, error } = await supabase.rpc('generate_base_slug', {
            business_name: businessName // Still send full name to RPC for proper processing
          });
          
          if (error) {
            console.error("Error generating base slug:", error);
            return;
          }
          
          if (data) {
            form.setValue("slug", data);
          }
        } catch (error) {
          console.error("Failed to generate base slug:", error);
        }
      }, 600); // Wait 600ms after user stops typing
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [form.watch("name")?.substring(0, 50), form]); // Only re-run when first 50 chars change


  const onSubmit = async (values: FormValues) => {
    // Check slug uniqueness one more time before submitting
    if (values.slug) {
      setChecking(true);
      const isUnique = await checkSlugUnique(values.slug);
      setChecking(false);
      
      if (!isUnique) {
        form.setError("slug", { 
          type: "manual", 
          message: "This URL is already taken in this community." 
        });
        return;
      }
    }
    
    // Make sure parent state is updated with the latest values
    updateFormData(values);
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Business Details</h2>
        <p className="text-muted-foreground">
          Tell us about your business. Provide the basic information that will help community members understand what you offer.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base">Business Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your business name" {...field} className="h-12 text-base" />
                </FormControl>
                <FormDescription className="text-sm mt-2">
                  This is how your business will appear to community members.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base">Business URL</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="business-url" 
                    {...field} 
                    className="h-12 text-base"
                  />
                </FormControl>
                <FormDescription className="text-sm mt-2">
                  Your business page will be available at: {" "}
                  <span className="inline-block bg-muted px-2 py-1 rounded font-mono font-medium text-foreground/90">
                    /service/{field.value || 'business-url'}
                  </span>
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="category_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base">Category</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-12 text-base">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id} className="text-base py-2.5">
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription className="text-sm mt-2">
                  Choose the category that best describes what you offer.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base">Business Description</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Describe what your business offers to the community" 
                    className="min-h-[150px] text-base"
                    {...field}
                  />
                </FormControl>
                <FormDescription className="text-sm mt-2">
                  Tell community members about your services, products, and what makes your business special.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <div className="flex justify-end pt-8">
            <Button type="submit" size="lg" className="px-8">Next</Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default BusinessBasicInfoForm;
